/**
 * Business logic for transforming FinalJob to DisplayJob
 */

import type { DisplayJob } from '../../../../types';
import type { FinalJob } from '../../../../lambda/types';
import {
  ENUM_FIELD_TRANSLATIONS,
  translateEnumValue,
} from '../../../../../utils/job/enum-translations';
import { coerceStringArrayField } from '../../../../../utils/seo/coerce-string-array-field';

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;
const LOCALE_SUFFIX_RE = /^(.+)_(en|fi|sv)$/;

/**
 * DisplayJob fields consumed with .map()/.length as string arrays. Records can carry them as a
 * single string or as processor junk (the literal "[object Object]" — seen in prod FinalJobs,
 * SSR-crashed job pages with 500s). Coerce so a bad shape can never take down the page, and so
 * a junk target-locale value counts as absent and the locale fallback below can still fill it.
 */
const STRING_ARRAY_FIELDS = [
  'responsibilities',
  'requirements',
  'benefits',
  'recruitmentProcess',
  'perks',
  'skillsAndTechnologies',
  'specialisations',
  'requiredLanguages',
  'optionalLanguages',
  'companyCoreValues',
  'companyAwardsAndCertifications',
] as const;

const STRING_ARRAY_FIELD_SET = new Set<string>(STRING_ARRAY_FIELDS);

/** Returns true when a value is considered absent (null, undefined, or empty string/array). */
function isAbsent(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Transforms a FinalJob to DisplayJob for a given locale.
 * Extracts language-specific fields and removes language suffixes.
 *
 * Any locale-variant field that is absent for the target locale is filled in
 * from a fallback locale (en → fi → sv, excluding the target) so that all
 * language versions of the page are equally complete.
 *
 * @param job - The FinalJob object to transform
 * @param lang - The target language code ('en', 'fi', or 'sv')
 * @returns A DisplayJob object with language-specific fields extracted
 */
export function transformJobForLanguage(job: FinalJob, lang: string): DisplayJob {
  const jobObj = job as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  const langSuffix = `_${lang}`;

  // First pass: extract target-locale fields + non-locale fields.
  for (const key in jobObj) {
    if (key.endsWith(langSuffix)) {
      const baseKey = key.slice(0, -langSuffix.length);
      result[baseKey] = jobObj[key];
    } else if (!key.match(/_en$|_sv$|_fi$/)) {
      result[key] = jobObj[key];
    }
  }

  // Sanitize list-shaped fields before the fallback pass so junk ("[object Object]", bare
  // strings) is normalized and an emptied-out target-locale value can still be backfilled.
  for (const field of STRING_ARRAY_FIELDS) {
    if (field in result) {
      result[field] = coerceStringArrayField(result[field]);
    }
  }

  // Second pass: for each locale-variant field still absent in the result,
  // try the remaining locales in preference order (en first, then the others).
  const fallbackLocales = ['en', ...SUPPORTED_LOCALES.filter(l => l !== 'en' && l !== lang)] as string[];

  // Collect the set of base keys that have any locale variant in the source job.
  const localeBaseKeys = new Set<string>();
  for (const key in jobObj) {
    const match = key.match(LOCALE_SUFFIX_RE);
    if (match) localeBaseKeys.add(match[1]);
  }

  for (const baseKey of localeBaseKeys) {
    if (!isAbsent(result[baseKey])) continue;
    for (const fallbackLang of fallbackLocales) {
      let candidate = jobObj[`${baseKey}_${fallbackLang}`];
      // List fields: a junk fallback candidate is as useless as an absent one.
      if (STRING_ARRAY_FIELD_SET.has(baseKey)) {
        candidate = coerceStringArrayField(candidate);
      }
      if (!isAbsent(candidate)) {
        // For known enum fields falling back from English, translate to the target locale
        // so users never see English enum strings on Finnish/Swedish pages.
        const enumMap = ENUM_FIELD_TRANSLATIONS[baseKey];
        if (enumMap && fallbackLang === 'en' && typeof candidate === 'string') {
          result[baseKey] = translateEnumValue(candidate, lang, enumMap);
        } else {
          result[baseKey] = candidate;
        }
        break;
      }
    }
  }

  if (result.objectID) {
    result.objectID = `${result.objectID}`;
  }

  return result as DisplayJob;
}

